import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {HttpClient} from '@angular/common/http';
import {finalize} from 'rxjs';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatExpansionModule} from '@angular/material/expansion';
import {TranslatePipe} from '@ngx-translate/core';
import {CaseHal} from '../../models/HAL/case-hal.model';
import {UserHal} from '../../models/HAL/user-hal.model';
import {UserSelectionComponent} from '../../components/user-selection/user-selection.component';
import {LoadingSpinnerService} from '../../services/loading-spinner.service';
import {SnackBarService} from '../../services/snackbar.service';
import {DialogService} from '../../services/dialog.service';
import {
  DynamicConfirmationDialogComponent
} from '../../components/dynamic-confirmation-dialog/dynamic-confirmation-dialog.component';

interface UserSelectionSlot {
  id: number;
  user?: UserHal;
  creating?: boolean;
  editing?: boolean;
  started?: boolean;
  failed?: boolean;
}

@Component({
  selector: 'app-online-identification',
  imports: [MatButton, MatIconButton, MatIcon, MatExpansionModule, TranslatePipe, UserSelectionComponent],
  templateUrl: './online-identification.component.html',
  styleUrl: './online-identification.scss'
})
export class OnlineIdentificationComponent implements OnInit {
  protected readonly case: CaseHal;
  protected selections: UserSelectionSlot[] = [{id: 0}];
  private nextSelectionId = 1;
  private starting = false;

  constructor(activatedRoute: ActivatedRoute,
              private readonly router: Router,
              private readonly httpClient: HttpClient,
              private readonly dialogService: DialogService,
              private readonly loadingSpinnerService: LoadingSpinnerService,
              private readonly snackBarService: SnackBarService) {
    this.case = new CaseHal(activatedRoute.snapshot.data['onlineIdentification']);
  }

  ngOnInit(): void {
    this.loadingSpinnerService.hide();
  }

  protected userSelected(selection: UserSelectionSlot, user: UserHal): void {
    if (this.selections.some(item => item !== selection && item.user?.identification === user.identification)) {
      this.selections = this.selections.filter(item => item !== selection);
      this.ensureEmptySelection();
      return;
    }
    selection.user = user;
    selection.started = false;
    selection.failed = false;
    this.ensureEmptySelection();
  }

  protected userDeselected(selection: UserSelectionSlot): void {
    this.selections = this.selections.filter(item => item !== selection);
    this.ensureEmptySelection();
  }

  protected get selectedUserIds(): string[] {
    return this.selections.flatMap(selection => selection.user && !selection.started ? [selection.user.identification] : []);
  }

  protected get canStart(): boolean {
    return !this.starting && this.selectedUserIds.length > 0 && this.selections.every(selection => !selection.creating && !selection.editing);
  }

  protected backToCase(): void {
    const dialogRef = this.dialogService.open(DynamicConfirmationDialogComponent, {
      data: {
        title: 'onlineIdentification.backToCaseModal.title',
        message: 'onlineIdentification.backToCaseModal.message',
        confirmButtonText: 'onlineIdentification.backToCaseModal.cancel',
        cancelButtonText: 'onlineIdentification.backToCaseModal.confirm'
      }
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadingSpinnerService.show();
        void this.router.navigate([this.case._links.getLink('self')?.getRoute()]);
      }
    });
  }

  protected startOnlineIdentification(): void {
    if (!this.canStart) {
      return;
    }
    this.starting = true;
    const requestedUserIds = this.selectedUserIds;
    this.loadingSpinnerService.show();
    this.httpClient.post<{userId: string, started: boolean}[]>(this.case._links.getLink('startOnlineIdentification')!.href,
      {userIds: requestedUserIds})
      .pipe(finalize(() => {
        this.starting = false;
        this.loadingSpinnerService.hide();
      }))
      .subscribe({
        next: response => {
          const results = Array.isArray(response) ? response : [];
          const startedIds = new Set(results.filter(result => result.started).map(result => result.userId));
          for (const selection of this.selections) {
            if (selection.user && requestedUserIds.includes(selection.user.identification)) {
              selection.started = startedIds.has(selection.user.identification);
              selection.failed = !selection.started;
            }
          }
          if (requestedUserIds.every(userId => startedIds.has(userId))) {
            this.snackBarService.open('onlineIdentification.snackbar.requestSuccess', 3);
            void this.router.navigate([this.case._links.getLink('self')?.getRoute()]);
          } else {
            const message = requestedUserIds.some(userId => startedIds.has(userId))
              ? 'onlineIdentification.snackbar.partialSuccess'
              : 'onlineIdentification.snackbar.requestError';
            this.snackBarService.open(message, 3, 'error');
          }
        },
        error: () => this.snackBarService.open('onlineIdentification.snackbar.requestError', 3, 'error')
      });
  }

  private ensureEmptySelection(): void {
    if (!this.selections.some(selection => !selection.user)) {
      this.selections.push({id: this.nextSelectionId++});
    }
  }
}
